import { Trans, useLingui } from "@lingui/react";
import type { WarpModal } from "@warp-ds/elements/modal";
import { Modal, ModalFooter, ModalHeader } from "@warp-ds/elements/react/modal";
import { Button } from "@warp-ds/react";
import { useImperativeHandle, useRef, useState } from "react";
import { useTracking } from "../../utils/pulseHelper.ts";

type ModalVariant = "sendMessage" | "reserveCar";

export type LoginModalHandle = {
  open: (variant: ModalVariant) => void;
};

export const LoginModal = ({ ref }: { ref?: React.Ref<LoginModalHandle> }) => {
  const modalRef = useRef<WarpModal>(null);
  const [nextUrl, setNextUrl] = useState("");
  const [variation, setVariation] = useState<ModalVariant>("sendMessage");
  const { i18n } = useLingui();

  const { trackLogin, trackCancelLoginModal } = useTracking();

  const openWith = (variant: ModalVariant) => {
    const { origin, pathname } = window.location;
    const url = new URL(`${origin}/auth/login`);
    const redirectUrl = variant === "sendMessage" ? `${origin}${pathname}?contact` : `${origin}${pathname}`;

    url.searchParams.set("redirectUrl", redirectUrl);
    url.searchParams.set("cancelUrl", `${origin}${pathname}`);

    setVariation(variant);
    setNextUrl(url.toString());
    modalRef.current?.open();
  };

  useImperativeHandle(ref, () => ({
    open: openWith,
  }));

  const handleCancel = () => {
    modalRef.current?.close();
  };

  const onHidden = () => {
    trackCancelLoginModal();
  };

  const loginModalTitle = i18n.t({ id: "dealer.loginModal.fomo.title", message: "You're not the only one interested" });

  const loginModalText = {
    sendMessage: i18n.t({
      id: "dealer.loginModal.fomo.description",
      message:
        "Log in and write to the seller before someone else strikes.\n" +
        "You can also save the ad or get notified when something similar appears.",
    }),
    reserveCar: i18n.t({
      id: "dealer.loginModal.reserve.description",
      message:
        "Log in to reserve the car before someone else does.\n" +
        "You can also save the ad or get notified when something similar appears.",
    }),
  };

  return (
    <Modal ref={modalRef} onHidden={onHidden}>
      <ModalHeader slot="header" title={loginModalTitle} />
      <div slot="content">
        <p>{loginModalText[variation]}</p>
      </div>
      <ModalFooter slot="footer">
        <div className="flex w-full md:w-[50%] ml-auto gap-8">
          <Button secondary quiet fullWidth onClick={handleCancel}>
            <Trans id="dealer.loginModal.cancel" message="Not now" />
          </Button>
          <Button primary fullWidth href={nextUrl} onClick={() => trackLogin()}>
            <Trans id="dealer.loginModal.logIn" message="Log in" />
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};
